import {
  Component,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  computed,
  inject,
} from "@angular/core";
import { Store } from "@ngrx/store";
import {
  Router,
  NavigationEnd,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { AppService } from "../../core/service/app.service";
import { HeaderService } from "../../core/service/header.service";
import { animate, style, transition, trigger } from "@angular/animations";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { IconPurchaseComponent } from "src/app/shared/icon/purchase-master/icon-purchase";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { MenuModule } from "headlessui-angular";
import { FormsModule } from "@angular/forms";
import { NgClass, NgComponentOutlet, AsyncPipe } from "@angular/common";

import { SharedModule } from "../../shared/shared.module";
import { VendorModalService } from "../../core/service/vendor-modal.service";
import { NotificationService } from "../notification.service";
import { AlertService } from "../../core/service/alert.service";
import { SocketService } from "../../core/service/socket.service";
import { HttpParams } from "@angular/common/http";
import { Subject, Subscription, takeUntil } from "rxjs";
import { AuthService } from "src/app/auth/auth.service";
import { ImageService } from "src/app/core/service/image.service";
import { ToastrService } from "ngx-toastr";
import { PermissionService } from "src/app/core/service/permission.service";
import { ComponentDeactivateService } from "src/app/core/service/component-deactivate.service";
import { ModuleAccessService } from "src/app/core/service/module-access.service";
import { SubModuleAccessService } from "src/app/core/service/sub-module-access.service";
import { NavbarService } from "src/app/core/service/navbar.service";
import { ChatUnreadCountService } from "src/app/pages/chat/service/chat-unread-count.service";

import { formatAlertTypeLabel } from "src/app/core/notifications/format-alert-type-label";
import {
  formatContractNotificationDateTime,
  formatContractNotificationTimeAgo,
  isContractAlertType,
  notificationListHeading,
  notificationMetaRows,
  shouldExcludeNotification,
  type NotificationMetaRow,
} from "src/app/core/notifications/notification-display.utils";
import { formatPreviewWithTables } from "src/app/shared/utils/message-preview-with-tables.util";
import { ChatService } from "src/app/pages/chat/service/chat.service";
import { ChatSocketService } from "src/app/pages/chat/service/chat-socket.service";
import { CurrencyService } from "src/app/core/service/currency.service";
@Component({
  selector: "header",
  templateUrl: "./header.component.html",
  animations: [
    trigger("toggleAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.95)" }),
        animate("100ms ease-out", style({ opacity: 1, transform: "scale(1)" })),
      ]),
      transition(":leave", [
        animate("75ms", style({ opacity: 0, transform: "scale(0.95)" })),
      ]),
    ]),
  ],
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    FormsModule,
    MenuModule,
    RouterLinkActive,
    TranslateModule,
    SharedModule,
    NgComponentOutlet,
    AsyncPipe,
],
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  store: any;
  search = false;
  headerConfig$ = this.headerService.headerConfig$;
  // Notifications data for dropdown
  // Using Angular components for icons (rendered via NgComponentOutlet)
  notifications!: Array<{
    id: number;
    title: string;
    alert_type: string;
    message: string;
    meta?: any; // e.g., Requested: Name or Expected by: Date
    meta_data?: any; // Alternative property name
    amount?: string; // optional currency text
    read: boolean;
    iconCmp: any; // Component class to render as icon
    time?: string;
    created_at?: string;
    action?: any;
  }>;
  notificationData: any[] = [];
  viewNotificationModel = signal<boolean>(false);
  isReadableNotificationDetails = signal<any>({});
  // Read/Unread filter state
  selectedNotifTab = signal<string>("all");
  tenantCurrency: string = "";
  tenantCurrencySymbol: string = "";
  // Profile-related properties
  profile_picture: string = "";
  profileImageUrl: string = "";
  profile_name: string = "";
  profile_email: string = "";
  userData: any;
  profileAvatarLoading = false;
  // Tenant modules
  tenantAttachedModules: any = {
    moduleHash: {},
    subModuleHash: {},
  };

  tenantAttachedModulesWithSubject: any = {
    moduleHash: {},
    subModuleHash: {},
  };
  private readonly destroy$ = new Subject<void>();
  // WebSocket notification subscription
  private notificationSubscription?: Subscription;
  currencies: { code: string; symbol: string }[] = [];
  /** True after user picks a currency from the header menu (not on tenant init). */
  currencyDropdownSelected = false;

  public readonly moduleAccessService = inject(ModuleAccessService);
  public readonly subModuleAccessService = inject(SubModuleAccessService);
  /** Admin Center nav when user has any related assigned module / sub-module. */
  readonly showAdminCenter = computed(() => {
    this.moduleAccessService.moduleCount();
    this.subModuleAccessService.parentModuleSlugs();
    return (
      this.moduleAccessService.hasAnyModuleAccess([
        "contract",
        "form-builder",
        "clause",
      ]) ||
      this.subModuleAccessService.hasSubModuleAccess(
        "event",
        "template-builder"
      ) ||
      this.subModuleAccessService.hasSubModuleAccess(
        "contract",
        "contract-template"
      ) ||
      this.subModuleAccessService.hasSubModuleAccess(
        "purchase-order",
        "purchase-order-list"
      )
    );
  });
 

  get filteredNotifications() {
    const visible = this.notifications.filter(
      (n) => !shouldExcludeNotification(n)
    );
    if (this.selectedNotifTab() === "read")
      return visible.filter((n) => n.read);
    if (this.selectedNotifTab() === "unread")
      return visible.filter((n) => !n.read);
    return visible;
  }

  /**
   * Get unread notification count
   */
  get unreadNotificationCount(): number {
    return this.notifications.filter(
      (n) => !n.read && !shouldExcludeNotification(n)
    ).length;
  }

  // Chat unread count
  totalChatUnreadCount: number = 0;
  private chatUnreadRefreshTimer?: ReturnType<typeof setTimeout>;

  messages = [
    {
      id: 1,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`
      ),
      title: "Congratulations!",
      message: "Your OS has been updated.",
      time: "1hr",
    },
    {
      id: 2,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`
      ),
      title: "Did you know?",
      message: "You can switch between artboards.",
      time: "2hr",
    },
    {
      id: 3,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`
      ),
      title: "Something went wrong!",
      message: "Send Reposrt",
      time: "2days",
    },
    {
      id: 4,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`
      ),
      title: "Warning",
      message: "Your password strength is low.",
      time: "5days",
    },
  ];

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router,
    private readonly appSetting: AppService,
    private readonly sanitizer: DomSanitizer,
    public headerService: HeaderService,
    private readonly vendorModalService: VendorModalService,
    private readonly notificationService: NotificationService,
    private readonly alertService: AlertService,
    private readonly socketService: SocketService,
    public authService: AuthService,
    public imageService: ImageService,
    public toastService: ToastrService,
    public permissionService: PermissionService,
    private readonly navbarService: NavbarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly chatUnreadCountService: ChatUnreadCountService,
    private readonly chatService: ChatService,
    private readonly chatSocketService: ChatSocketService,
    public currencyService: CurrencyService,
    private readonly componentDeactivateService: ComponentDeactivateService,
  ) {
    // Build notifications using the icon components
    this.notifications = [];
  }
  initStore() {
    this.storeData
      .select((d) => d.index)
      .pipe(takeUntil(this.destroy$))
      .subscribe((d) => {
        this.store = d;
      });
  }

  ngOnInit() {
    this.initStore();
    this.userData = this.authService.getUserData();
    const tenantCurrency = this.userData?.tenantInfo?.currency;
    const normalizedTenant = tenantCurrency?.trim().toUpperCase();
    this.tenantCurrency = normalizedTenant;
    this.tenantCurrencySymbol = this.getCurrencySymbol(normalizedTenant);
    const tenantSymbol = normalizedTenant
      ? this.getCurrencySymbol(normalizedTenant)
      : undefined;
    this.currencyService.initializeFromTenant(normalizedTenant, tenantSymbol);
    this.currencyDropdownSelected = false;
    this.currencies = [{ code: 'INR', symbol: '₹' },{ code: 'USD', symbol: '$' }];
    if (normalizedTenant) {
      const existingIndex = this.currencies.findIndex(
        (currency) => currency.code.toUpperCase() === normalizedTenant,
      );
      if (existingIndex >= 0) {
        this.currencies.splice(existingIndex, 1);
      }
    }
    this.getNotifications();
    this.setActiveDropdown();
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setActiveDropdown();
      }
    });
    this.subscribeToWebSocketNotifications();
    this.loadProfileImage();

    // Initial load for header chat dot
    this.loadHeaderChatUnreadCount();

    this.chatUnreadCountService.getTotalUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.totalChatUnreadCount = count;
        this.cdr.detectChanges();
      });

    // Real-time updates for header chat dot
    this.chatSocketService
      .onChatMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.scheduleHeaderChatUnreadRefresh();
      });

    this.chatSocketService
      .onMessagesRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.scheduleHeaderChatUnreadRefresh();
      });

    this.authService.profileDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profileData) => {
        if (profileData) {
          this.profile_picture = profileData.profile_picture;
          this.profile_name = profileData.name;
          this.profile_email = profileData.email_id;
          this.loadProfileImage();
        } else {
          this.profile_picture = this.userData?.profile_picture;
          this.profile_name = this.userData?.name;
          this.profile_email = this.userData?.email_id;
          this.loadProfileImage();
        }
      });
  }
  getCurrencySymbol(currencyCode: string): string {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode,
    }).formatToParts(0);
  
    return (
      parts.find(part => part.type === 'currency')?.value ??
      currencyCode
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.chatUnreadRefreshTimer) {
      clearTimeout(this.chatUnreadRefreshTimer);
    }
  }

  private scheduleHeaderChatUnreadRefresh(): void {
    // Debounce bursty socket events
    if (this.chatUnreadRefreshTimer) {
      clearTimeout(this.chatUnreadRefreshTimer);
    }
    this.chatUnreadRefreshTimer = setTimeout(() => {
      this.loadHeaderChatUnreadCount();
    }, 600);
  }

  private loadHeaderChatUnreadCount(): void {
    const params = new HttpParams().set("page", "1").set("page_size", "50");
    this.chatService
      .getConversations(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.status && response?.data?.conversations) {
            const totalUnread =
              this.chatUnreadCountService.calculateTotalUnreadCount(
                response.data.conversations,
              );
            this.chatUnreadCountService.updateTotalUnreadCount(totalUnread);
          } else {
            this.chatUnreadCountService.updateTotalUnreadCount(0);
          }
        },
        error: () => {
          // Don't break header if API fails
          this.chatUnreadCountService.updateTotalUnreadCount(0);
        },
      });
  }

  /**
   * Subscribe to WebSocket notifications
   */
  subscribeToWebSocketNotifications() {
    this.notificationSubscription =
      this.socketService.socketNotificationData.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
        if (data?.notification) {
          this.handleIncomingNotification(data);
        }
      });
  }

  /**
   * Handle incoming WebSocket notification
   */
  handleIncomingNotification(data: any) {
    const notification = data.notification;

    if (
      shouldExcludeNotification({
        alert_type: notification.alertType || this.mapNotificationType(data.type),
        title: notification.title,
      })
    ) {
      return;
    }

    // Map WebSocket notification to the format expected by the UI
    const mappedNotification = {
      id: notification.id || Date.now(), // Use timestamp as ID if not provided
      alert_type: notification.alertType || this.mapNotificationType(data.type),
      title: notification.title,
      message: this.resolveNotificationMessage(notification),
      meta: notification.metadata || {},
      meta_data: notification.metadata || {},
      read: notification.isRead || false,
      iconCmp: IconPurchaseComponent, // Default icon component
      time: notification.time || new Date().toLocaleString(),
      created_at: notification.createdAt || new Date().toISOString(),
      action: notification.action,
    };

    // Add notification to the beginning of the list
    this.notifications = [mappedNotification, ...this.notifications];

    // Show toast/alert notification (use preview formatting for table payloads)
    // this.alertService.showAlert(
    //   notification.title,
    //   notification.message,
    //   "info"
    // );

    // Previous behavior (always toast + sound for every notification — may duplicate Vendor Quotes success modal on bid accept).
    // To revert: uncomment below and remove the `skipBidAcceptedToast` block that follows.
    // this.toastService.info(
    //   this.notificationPreview(mappedNotification.message),
    //   notification.title,
    // );
    // this.playNotificationSound();

    // Skip toast/sound for bid accepted — Vendor Quotes / item-view already shows success modal on accept;
    // the same action also emits `in_app_notification`, which would duplicate UX (modal + toast).
    // Skip toast/sound for bid rejected — same duplicate (success modal from rejectBid + socket `in_app_notification`).

    const bidAcceptedToastTitle =
      String(notification?.title ?? "")
        .trim()
        .toLowerCase() === "bid accepted";
    const bidAcceptedNotificationType =
      String(data?.type ?? "")
        .toUpperCase() === "BID_ACCEPTED";
    const bidAcceptedAlertType =
      String(notification?.alertType ?? "")
        .toUpperCase() === "BID_ACCEPTED";
    const skipBidAcceptedToast =
      bidAcceptedToastTitle ||
      bidAcceptedNotificationType ||
      bidAcceptedAlertType;

    const bidRejectedToastTitle =
      String(notification?.title ?? "")
        .trim()
        .toLowerCase() === "bid rejected";
    const bidRejectedNotificationType =
      String(data?.type ?? "")
        .toUpperCase() === "BID_REJECTED";
    const bidRejectedAlertType =
      String(notification?.alertType ?? "")
        .toUpperCase() === "BID_REJECTED";
    const skipBidRejectedToast =
      bidRejectedToastTitle ||
      bidRejectedNotificationType ||
      bidRejectedAlertType;

    const skipBidVendorQuoteToast =
      skipBidAcceptedToast || skipBidRejectedToast;
    if (!skipBidVendorQuoteToast) {
      this.toastService.info(
        this.notificationPreview(mappedNotification.message),
        notification.title,
      );
    }
    // Play notification sound (optional)
    if (!skipBidVendorQuoteToast) {
      this.playNotificationSound();
    }

    // If action is provided, optionally navigate
    if (notification.action?.type === "navigate" && notification.action?.url) {
      // Optionally auto-navigate or show a clickable link
      console.log("📬 Notification action:", notification.action);
    }
  }

  /**
   * Map WebSocket notification type to alert type
   */
  mapNotificationType(type: string): string {
    const typeMap: { [key: string]: string } = {
      BID_NOTIFICATION: "BID",
      EVENT_NOTIFICATION: "AUCTION",
      QUOTATION_NOTIFICATION: "QUOTATION",
      COUNTER_OFFER_NOTIFICATION: "COUNTER_OFFER",
      SYSTEM_NOTIFICATION: "SYSTEM",
    };
    return typeMap[type] || "SYSTEM";
  }

  /**
   * Play notification sound
   */
  playNotificationSound() {
    try {
      const audio = new Audio("/assets/sounds/notification.mp3");
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore errors if audio file doesn't exist or can't play
      });
    } catch (error) {
      // Ignore errors
    }
  }

  private isBrowserContext(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  setActiveDropdown() {
    if (!this.isBrowserContext()) return;
    const selector = document.querySelector(
      'ul.horizontal-menu a[routerLink="' + window.location.pathname + '"]'
    );
    if (selector) {
      selector.classList.add("active");
      const all: any = document.querySelectorAll(
        "ul.horizontal-menu .nav-link.active"
      );
      for (let i = 0; i < all.length; i++) {
        all[0]?.classList.remove("active");
      }
      const ul: any = selector.closest("ul.sub-menu");
      if (ul) {
        let ele: any = ul.closest("li.menu").querySelectorAll(".nav-link");
        if (ele) {
          ele = ele[0];
          setTimeout(() => {
            ele?.classList.add("active");
          });
        }
      }
    }
  }

  removeNotification(value: number) {
    this.notifications = this.notifications.filter((d) => d.id !== value);
  }

  removeMessage(value: number) {
    this.messages = this.messages.filter((d) => d.id !== value);
  }

  changeLanguage(item: any) {
    this.translate.use(item.code);
    this.appSetting.toggleLanguage(item);
    if (this.store.locale?.toLowerCase() === "ae") {
      this.storeData.dispatch({ type: "toggleRTL", payload: "rtl" });
    } else {
      this.storeData.dispatch({ type: "toggleRTL", payload: "ltr" });
    }
    this.reloadPage();
  }

  private reloadPage(location: Location = window.location): void {
    if (typeof window !== 'undefined') {
      location.reload();
    }
  }

  /**
   * Open the invite vendor modal
   * Opens modal directly without navigation
   */
  openInviteVendorModal() {
    // Trigger the modal to open (works from any page)
    this.vendorModalService.openInviteVendorModal();
  }

  /**
   * Fetch notifications
   */
  getParams(): HttpParams {
    let params = new HttpParams().append("filter", this.selectedNotifTab());

    return params;
  }
  getNotifications(): void {
    // Guard: skip API call if no auth token to prevent 401 redirect loop
    if (typeof localStorage === 'undefined' || !localStorage.getItem('adminAuthToken')) {
      return;
    }
    this.notificationService.fetchNotifications(this.getParams()).subscribe({
      next: (res: any) => {
        if (res?.status) {
                 this.notificationData = res?.data?.allAlerts;

          this.notifications = this.notificationData
            .filter(
              (notification: any) => !shouldExcludeNotification(notification)
            )
            .map((notification: any) => ({
              id: notification.id,
              alert_type: notification.alert_type,
              title: notification.title,
              meta: notification.meta_data,
              read: !!notification.is_read,
              message: this.resolveNotificationMessage(notification),
              iconCmp: IconPurchaseComponent,
              created_at: notification.created_at,
            }));
        }
        
      },

      error: (err: any) => {
        this.alertService.showAlert(
          "Error",
          err?.error?.message || err?.error?.error,
          "error"
        );
      },
    });
  }

  notificationType(type: string) {
    this.selectedNotifTab.set(type);
    this.getNotifications();
  }

  notificationsTab(notificationType: string = "all") {
    switch (notificationType) {
      case "read":
      case "unread":
        this.selectedNotifTab.set(notificationType);
        this.getNotifications();
        break;
      default:
        this.selectedNotifTab.set("all");
        this.getNotifications();
    }
  }

  fetchByIdNotification(id: number) {
    this.viewNotificationModel.set(true);
    this.notificationService.fetchByIdNotification(id).subscribe({
      next: (res) => {
        if (res.status) {
          this.notifications = this.notifications.map((notification) => {
            if (notification.id === id) {
              notification.read = true;
            }
            return notification;
          });
          this.isReadableNotificationDetails.set(res.data);

          this.getNotifications();
        }
      },
      error: (error) => {
        this.alertService.showAlert(
          "Error",
          error?.error?.message || error?.error?.error,
          "error"
        );
      },
    });
  }
  formatData(date: string) {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  formatTitle(type: string): string {
    return formatAlertTypeLabel(type);
  }

  /** Bell dropdown row title (handles CONTRACT #id). */
  compactNotificationHeading(notification: {
    alert_type?: string;
    meta?: any;
    meta_data?: any;
  }): string {
    return notificationListHeading({
      alert_type: notification?.alert_type,
      meta: notification?.meta ?? notification?.meta_data,
    });
  }

  /** Detail modal: flattened meta for every alert type except UPLOAD (custom UI). */
  modalMetaRows(): NotificationMetaRow[] {
    const d = this.isReadableNotificationDetails();
    if (!d?.meta_data || d.alert_type === "UPLOAD") return [];
    return notificationMetaRows(d.meta_data, d.alert_type);
  }

  notificationTimeLabel(notification?: {
    alert_type?: string;
    created_at?: string;
  } | null): string {
    if (isContractAlertType(notification?.alert_type)) {
      return formatContractNotificationDateTime(notification?.created_at);
    }
    return this.timeAgo(notification?.created_at ?? "");
  }

  notificationTimeAgo(notification?: {
    alert_type?: string;
    created_at?: string;
  } | null): string {
    if (isContractAlertType(notification?.alert_type)) {
      return formatContractNotificationTimeAgo(notification?.created_at);
    }
    return "";
  }

  timeAgo(date: string): string {
    if (date) {
      const time = new Date(date).getTime();
      const now = new Date().getTime();
      const diff = Math.floor((now - time) / 1000);

      if (diff < 60) return "Just now";
      if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
      return `${Math.floor(diff / 86400)} day(s) ago`;
    }
    return "-";
  }

  getFileErrorModule(title: string): string {
    const match = title.match(/\b(UOM|Item|Category)\b/i);
    return match ? `${match[0]}` : "";
  }

  closeViewNotificationModal() {
    this.viewNotificationModel.set(false);
  }

  getAllNotification() {
    this.router.navigateByUrl("/notification-master");
  }

  /**
   * Handle notification click - navigate or fetch details
   */
  handleNotificationClick(notification: any) {
    // If notification has an action, use it for navigation
    if (notification.action?.type === "navigate" && notification.action?.url) {
      const url = notification.action.url;
      const params = notification.action.params || {};

      // Navigate to the URL with optional params
      if (Object.keys(params).length > 0) {
        this.router.navigate([url], { queryParams: params });
      } else {
        this.router.navigateByUrl(url);
      }

      // Mark as read if it has an ID
      if (notification.id) {
        this.fetchByIdNotification(notification.id);
      }
    } else if (notification.id) {
      // Fallback to existing behavior - fetch notification details
      this.fetchByIdNotification(notification.id);
    }
  }

  /**
   * Load profile image using ImageService
   */
  loadProfileImage() {
    // Choose picture from observable override or stored user data
    let effectivePicture: string | null;
    if (this.profile_picture === null) {
      effectivePicture = null;
    } else if (this.profile_picture && this.profile_picture.trim() !== "") {
      effectivePicture = this.profile_picture;
    } else {
      effectivePicture = this.userData?.profile_picture || null;
    }
    this.profileAvatarLoading = true;
    if (effectivePicture && this.userData?.id) {
      this.imageService.getPresignedImageUrl('users', effectivePicture, 'profile-picture', this.userData.id).subscribe({
        next: (url) => {
          this.profileImageUrl = url;
        },
        error: (error) => {
          console.error('Error loading profile image:', error);
        }
      })
    } else {
      this.profileImageUrl = "";
    }
    this.cdr.detectChanges();
  }

  /**
   * Load profile details
   */
  loadProfileDetails(name: string, email_id: string) {
    this.authService.getUserPresignedDetails(name, email_id).subscribe({
      next: (res) => {
        this.profile_email = res.email_id;
        this.profile_name = res.name;
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  /**
   * Load tenant modules from localStorage
   */
  loadTenantModules(): void {
    this.tenantAttachedModules =
      this.navbarService.getTenantAttachModulesFromSession();
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.navbarService.tenantAttachedModules$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.tenantAttachedModulesWithSubject = data;
        this.cdr.detectChanges();
      });
  }

  /**
   * Check if a module is enabled for the tenant
   * @param moduleSlug - The slug of the module to check
   * @returns boolean - true if module is enabled
   */
  isModuleEnabled(moduleSlug: string): boolean {
    // If nothing is loaded yet → allow access
    if (
      !this.tenantAttachedModules?.moduleHash &&
      !this.tenantAttachedModulesWithSubject?.moduleHash
    ) {
      return true;
    }

    // Priority 1: subject-based module access (if available)
    if (this.tenantAttachedModulesWithSubject?.moduleHash) {
      return !!this.tenantAttachedModulesWithSubject.moduleHash[moduleSlug];
    }

    // Priority 2: normal module access (fallback)
    return !!this.tenantAttachedModules?.moduleHash?.[moduleSlug];
  }

  /**
   * Check if a sub-module is enabled for the tenant
   * @param moduleSlug - The slug of the parent module
   * @param subModuleSlug - The slug of the sub-module to check
   * @returns boolean - true if sub-module is enabled
   */
  isSubModuleEnabled(moduleSlug: string, subModuleSlug: string): boolean {
    // If nothing is loaded yet → allow access
    if (
      !this.tenantAttachedModules?.subModuleHash &&
      !this.tenantAttachedModulesWithSubject?.subModuleHash
    ) {
      return true;
    }

    // Priority 1: subject-based sub-module access (if available)
    if (this.tenantAttachedModulesWithSubject?.subModuleHash) {
      return !!this.tenantAttachedModulesWithSubject.subModuleHash?.[
        moduleSlug
      ]?.[subModuleSlug];
    }

    // Priority 2: normal sub-module access (fallback)
    return !!this.tenantAttachedModules?.subModuleHash?.[moduleSlug]?.[
      subModuleSlug
    ];
  }

  /**
   * Sign out user
   */
  async signOut() {
    const canLeave = await this.componentDeactivateService.confirmLeave();
    if (canLeave) {
      this.authService.logout();
    }
  }

  allNotifications() {
    this.router.navigateByUrl("/notification-master");
  }

  isTableMessage(message: string): boolean {
    return message.includes('<table');
}
sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  notificationPreview(message: string | null | undefined): string {
    return formatPreviewWithTables(message).preview;
  }

  /**
   * Backend chat alerts are not consistent: some send message as `message`,
   * others embed it in `meta_data/metadata` (e.g., `content`, `chat_message`).
   * This keeps dropdown preview working for vendor->admin chat notifications.
   */
  private resolveNotificationMessage(notification: any): string {
    const direct = notification?.message ?? notification?.body ?? notification?.content;
    if (typeof direct === "string" && direct.trim()) return direct;

    const meta = notification?.meta_data ?? notification?.metadata ?? notification?.meta ?? {};
    const candidates = [
      meta?.message,
      meta?.content,
      meta?.chat_message,
      meta?.chatMessage,
      meta?.last_message,
      meta?.lastMessage,
      meta?.text,
      meta?.html,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c;
    }
    return "";
  }

  changeCurrency(symbol: string, code: string): void {
    this.currencyDropdownSelected = true;
    this.currencyService.setSelectedCurrency(code, symbol);
    this.cdr.markForCheck();
  }

  clearCurrencySelection(): void {
    this.currencyDropdownSelected = false;
    this.currencyService.clearCurrencySelection();
    this.cdr.markForCheck();
  }

  showCurrencyPlaceholder(): boolean {
    return !this.currencyDropdownSelected;
  }

  currencyCode(): string {
    return this.currencyService.selectedCurrency().code;
  }

  currencySymbol(): string {
    return this.currencyService.selectedCurrency().symbol;
  }
}
